import I18n from 'I18n';
import FormattedList from 'I18n/components/FormattedList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import PortalIdParser from 'PortalIdParser';
import UILink from 'UIComponents/link/UILink';
import { AutomationFieldType } from 'automation-ui-action-features/actions/extension/constants/AutomationFieldType';
import AutomatedEmailName from 'automation-ui-action-features/actions/extension/render-to-string/AutomatedEmailName';
import { SkillLabels } from 'automation-ui-action-features/actions/extension/render-to-string/SkillLabels';
import { TeamsSelectValue } from 'automation-ui-action-features/actions/extension/render-to-string/TeamsSelectValue';
import sanitizeWithExternalLinks from 'automation-ui-action-features/actions/extension/utils/sanitizeWithExternalLinks';
import { parseRoutingTeamsValue, parseWhatsAppTemplateValue } from 'automation-ui-action-features/actions/extension/utils/valueParsers';
import PersonalizedTextView from 'automation-ui-action-features/personalized-text/PersonalizedTextView';
import AppContext from 'automation-ui-core/context/AppContext';
import PropertyFieldTypes from 'automation-ui-flow-core/fields/PropertyFieldTypes';
import DataSourcePropertyName from 'automation-ui-formula-editor/data-sources/DataSourcePropertyName';
import { isDataToken } from 'automation-ui-formula-editor/data-sources/utils/isDataToken';
import FormattedDateTimeWithZones from 'automation-ui-i18n/FormattedDateTimeWithZones';
import PropertyName from 'automation-ui-references/object-type-properties/PropertyName';
import { objectListUrl } from 'automation-ui-references/routing/hubspotUrls';
import { STATIC_INBOUND_DB_LIST } from 'customer-data-objects/externalOptions/ExternalOptionsReferenceTypes';
import AutomatedSmsName from 'messaging-editing-lib/preview/Automation/AutomatedSmsName';
import { useContext } from 'react';
import ReferenceLiteName from 'reference-resolvers-lite/components/ReferenceLiteName';
import DataSourceName from 'automation-ui-formula-editor/data-sources/DataSourceName';
import { jsx as _jsx } from "react/jsx-runtime";
import { Fragment as _Fragment } from "react/jsx-runtime";
const referenceTypeRequiresObjectTypeId = referenceType => STATIC_INBOUND_DB_LIST === referenceType;
const getReferenceUrl = (referenceId, referenceType) => {
  switch (referenceType) {
    case STATIC_INBOUND_DB_LIST:
      return objectListUrl(referenceId);
    default:
      return undefined;
  }
};
const FieldString = ({
  automationFieldType,
  externalOptionsReferenceType,
  fieldType,
  labelMap = {},
  objectTypeId,
  value
}) => {
  const {
    sourceApp
  } = useContext(AppContext);
  const stringValue = typeof value === 'string' ? value : '';
  const stringArray = Array.isArray(value) ? value : [stringValue];
  const maybeNumber = Number.parseInt(stringValue, 10);
  switch (automationFieldType) {
    case AutomationFieldType.AUTOMATED_EMAIL:
      return /*#__PURE__*/_jsx(AutomatedEmailName, {
        emailId: stringValue,
        sourceApp: sourceApp
      });
    case AutomationFieldType.AUTOMATED_SMS:
      return /*#__PURE__*/_jsx(AutomatedSmsName, {
        rootMicId: stringValue,
        use: "link"
      });
    case AutomationFieldType.OWNER_PROPERTY:
      return /*#__PURE__*/_jsx(PropertyName, {
        objectTypeId: objectTypeId,
        propertyName: stringValue
      });
    case AutomationFieldType.ROUTING_TEAMS:
      {
        const {
          parsedValue
        } = parseRoutingTeamsValue(stringValue);
        return /*#__PURE__*/_jsx(TeamsSelectValue, {
          value: parsedValue || []
        });
      }
    case AutomationFieldType.SKILLS:
      return /*#__PURE__*/_jsx(SkillLabels, {
        values: stringArray
      });
    case AutomationFieldType.STEPPER:
      return /*#__PURE__*/_jsx(_Fragment, {
        children: stringValue
      });
    case AutomationFieldType.WHATSAPP_TEMPLATE:
      {
        const {
          parsedValue,
          parseError
        } = parseWhatsAppTemplateValue(stringValue);
        const {
          templateMappingId,
          templateName
        } = parsedValue || {};
        if (parseError || !templateName || !templateMappingId) {
          return /*#__PURE__*/_jsx(FormattedMessage, {
            message: "automation-ui-action-features.action-features.extensions.whatsapp-template-fields.invalid-template"
          });
        }
        const portalId = PortalIdParser.get();
        return /*#__PURE__*/_jsx(UILink, {
          external: true,
          href: `/messaging-templates/${portalId}/whatsapp/template/mapping/${templateMappingId}`,
          children: templateName
        });
      }
    case AutomationFieldType.SINGLE_OBJECT_COORDINATES:
      return /*#__PURE__*/_jsx(DataSourceName, {
        dataSourcePrefix: stringValue
      });
    default:
      break;
  }
  switch (fieldType) {
    case PropertyFieldTypes.BOOL:
      return /*#__PURE__*/_jsx(_Fragment, {
        children: stringValue || 'false'
      });
    case PropertyFieldTypes.CHECKBOX:
    case PropertyFieldTypes.ENUM:
    case PropertyFieldTypes.RADIO:
      return /*#__PURE__*/_jsx(FormattedList, {
        children: externalOptionsReferenceType ? stringArray.map(_value => /*#__PURE__*/_jsx(ReferenceLiteName, {
          resolverOptions: {
            byIdIncludeDeleted: false
          },
          href: getReferenceUrl(_value, externalOptionsReferenceType),
          id: _value,
          objectTypeId: referenceTypeRequiresObjectTypeId(externalOptionsReferenceType) ? objectTypeId : undefined,
          referenceType: externalOptionsReferenceType
        }, _value)) : stringArray.filter(_value => labelMap[_value]) // Filter invalid options.
        .map(_value => labelMap[_value])
      });
    case PropertyFieldTypes.DATE:
    case PropertyFieldTypes.DATETIME:
      {
        if (isDataToken(stringValue)) {
          return /*#__PURE__*/_jsx(DataSourcePropertyName, {
            token: stringValue
          });
        }
        const format = fieldType === PropertyFieldTypes.DATETIME ? 'lll' : 'll';
        const momentObject = I18n.moment(
        /**
         * NOTE(bheng): Unix timestamps are in seconds, not milliseconds.
         *   Not ideal, but in practical applications we can assume strings less
         *   than 12 characters are in seconds.
         */
        stringValue.length < 12 ? maybeNumber * 1000 : maybeNumber);
        // Prefer to display the date time in the portal's timezone so users don't
        // have to do the mental math.
        const timezoneOffset = I18n.moment.portalTz().format('ZZ');
        momentObject.utcOffset(timezoneOffset);
        return /*#__PURE__*/_jsx(FormattedDateTimeWithZones, {
          format: format,
          hideSeconds: true,
          value: momentObject
        });
      }
    case PropertyFieldTypes.HTML:
      return /*#__PURE__*/_jsx(_Fragment, {
        children: sanitizeWithExternalLinks(stringValue)
      });
    case PropertyFieldTypes.MULTI_LINE_STRING:
    case PropertyFieldTypes.SINGLE_LINE_STRING:
      return objectTypeId ? /*#__PURE__*/_jsx(PersonalizedTextView, {
        propertyTypeId: objectTypeId,
        text: stringValue
      }) : null;
    case PropertyFieldTypes.NUMBER:
    case PropertyFieldTypes.PHONE_NUMBER:
      {
        if (isDataToken(stringValue)) {
          return /*#__PURE__*/_jsx(DataSourcePropertyName, {
            token: stringValue
          });
        }
        // NOTE: parseInt(val) and parseFloat(val) convert a string to a number (ex "+1 (234) 567-8910" => 1), but Number(val) correctly returns NaN
        const numberValue = Number(stringValue);
        return isNaN(numberValue) ? /*#__PURE__*/_jsx(_Fragment, {
          children: stringValue
        }) : /*#__PURE__*/_jsx(_Fragment, {
          children: I18n.formatNumber(numberValue)
        });
      }
    default:
      throw new Error(`Unrecognized field: automationFieldType: ${automationFieldType}, fieldType: ${fieldType}. Please add it to FieldString.`);
  }
};
export default FieldString;